import { theme } from '../../styles/theme';
import type { TransitType } from './MapRenderer';

interface TransitFilterProps {
  activeType: TransitType | null;
  availableTypes: Set<TransitType>;
  nearbyTypes?: Set<TransitType>;
  onSelect: (type: TransitType | null) => void;
  showZones: boolean;
  onToggleZones: () => void;
  showRoads: boolean;
  onToggleRoads: () => void;
}

const TRANSIT_LABELS: Record<TransitType, { label: string; icon: string }> = {
  subway: { label: 'SUBWAY', icon: '🚇' },
  tram: { label: 'TRAM', icon: '🚊' },
  bus: { label: 'BUS', icon: '🚌' },
  ferry: { label: 'FERRY', icon: '⛴' },
};

const ALL_TYPES: TransitType[] = ['subway', 'tram', 'bus', 'ferry'];

export function TransitFilter({ activeType, availableTypes, nearbyTypes, onSelect, showZones, onToggleZones, showRoads, onToggleRoads }: TransitFilterProps) {
  const types = ALL_TYPES.filter(t => availableTypes.has(t));

  return (
    <div style={styles.container}>
      {types.length > 0 && (
        <>
          <div style={styles.label}>TRANSIT</div>
          {types.map(type => {
            const active = activeType === type;
            const nearby = nearbyTypes?.has(type) && !active;
            const info = TRANSIT_LABELS[type];
            return (
              <button
                key={type}
                onClick={() => onSelect(active ? null : type)}
                className={`transit-tab ${active ? 'transit-tab--active' : 'transit-tab--inactive'}`}
                style={nearby ? {
                  boxShadow: '0 0 6px rgba(170, 136, 255, 0.5)',
                  border: '1px solid rgba(170, 136, 255, 0.7)',
                } : undefined}
                title={nearby ? `${info.label} — stop nearby` : info.label}
              >
                <span>{info.label}</span>
              </button>
            );
          })}
        </>
      )}
      <div style={{ ...styles.label, marginTop: types.length > 0 ? '10px' : '0' }}>OVERLAY</div>
      <button
        onClick={onToggleZones}
        className={`transit-tab ${showZones ? 'transit-tab--active' : 'transit-tab--inactive'}`}
        title="Toggle zone overlay (Q)"
      >
        <span>ZONES (Q)</span>
      </button>
      <button
        onClick={onToggleRoads}
        className={`transit-tab ${showRoads ? 'transit-tab--active' : 'transit-tab--inactive'}`}
        title="Toggle road overlay (R)"
      >
        <span>ROADS (R)</span>
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'absolute',
    right: 0,
    top: '35%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    zIndex: 10,
    background: 'rgba(8, 12, 20, 0.85)',
    borderRadius: '6px 0 0 6px',
    padding: '10px 12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRight: 'none',
    backdropFilter: 'blur(4px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
  label: {
    fontSize: '9px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    textAlign: 'center',
    marginBottom: '2px',
    fontWeight: 600,
  },
};
